/**
 * A lookup CODE derived from the label a person typed.
 *
 * Every vocabulary row needs a `code` matching `^[A-Z][A-Z0-9_]{1,63}$` — the
 * same regex in the CRM, scheduling and feedback services — and it is
 * IMMUTABLE after creation. That is a fine thing to ask an administrator for on
 * a settings screen, and a terrible thing to ask a rep who opened the calendar
 * to book a meeting and found the type picker empty. So the inline creator asks
 * for the half a human reads (the Persian label) and derives the other half.
 *
 * This mirrors `domain/import_service.py::source_code` in the CRM, which faces
 * the identical problem with congress names from a spreadsheet: «کنگره قلب
 * ۱۴۰۵» has no ASCII letters at all and cannot produce a code, so a non-Latin
 * label gets a transliteration-free fallback and keeps its original text as the
 * LABEL. Transliterating Persian into ASCII would invent a spelling nobody
 * chose and two people would spell the same congress differently.
 *
 * The hash is FNV-1a, not `Math.random()` or a timestamp: the same label must
 * derive the same code every time, so two people adding «جلسه حضوری» minutes
 * apart collide on the server's unique index and the second one is told the
 * type already exists — rather than quietly producing a second row that means
 * exactly the same thing. (The CRM importer reaches for `hashlib` over Python's
 * salted builtin `hash()` for this same reason; CLAUDE.md §15.)
 */

/** FNV-1a over the string's code points, as 8 uppercase hex digits. */
function stableHash(text) {
  let hash = 0x811c9dc5;
  for (const char of text) {
    hash ^= char.codePointAt(0);
    // The classic FNV prime, multiplied in 32-bit space via Math.imul.
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).toUpperCase().padStart(8, '0');
}

/**
 * @param label   what the user typed, in any script
 * @param prefix  used only when the label yields no usable ASCII
 * @returns a code the services will accept
 */
export function codeFromLabel(label, prefix = 'CUSTOM') {
  const text = (label ?? '').trim();
  if (!text) return '';

  const ascii = text
    .normalize('NFKD')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();

  // `A` alone is a valid identifier but not a valid code: the regex demands at
  // least two characters.
  if (/^[A-Z]/.test(ascii) && ascii.length >= 2) return ascii.slice(0, 64);
  if (ascii) return `${prefix}_${ascii}`.slice(0, 64);
  return `${prefix}_${stableHash(text)}`;
}
